import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useReducer } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { FunctionalLoader, IntentBehaviorProvider, useDidUpdate, useOverlayContext } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { WithdrawnForEditingPage } from '~/components/WithdrawnForEditingPage/WithdrawnForEditingPage';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { getIntentBehavior } from '~/infra/access-control';
import { relationshipModel } from '~/models';
import { requestAccountantAccessModel } from '~/models/request-accountant-access';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { selectors } from '~/store/state-selectors';
import { clientOnboardingNavigateStateReducer, COBStep, isAllowedToGoBack, PreviewState } from './ClientOnboarding.utils';
import { ClientOnboardingInner as ClientOnboardingInnerV1 } from './ClientOnboardingInner';
import { ClientOnboardingInnerV2 } from './ClientOnboardingInnerV2/ClientOnboardingInnerV2';
import { CobBrandingProvider } from './CobBrandingContext';
import { CobStorageProvider, useCobStorage } from './CobStorageContext';
import { changeBusinessLoader } from './WelcomeStep/welcomeStepUtils';
export function ClientOnboarding(props) {
    const userRole = useStore(selectors.user.role);
    const userId = useStore(selectors.user.id);
    const relationshipScope = {
        id: props.relationship.id,
        isApproved: relationshipModel.isApproved(props.relationship)
    };
    const { CLIENT_ONBOARDING_NEW_FLOW } = useFeatureFlags();
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: relationshipScope,
        children: /*#__PURE__*/ _jsx(IntentBehaviorProvider, {
            value: _object_spread({}, getIntentBehavior(userRole, {
                relationshipState: {
                    relationship: props.relationship,
                    isPreviewMode: !!props.previewState
                },
                userId
            })),
            children: CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/ _jsx(ClientOnboardingInnerV2, _object_spread({}, props)) : /*#__PURE__*/ _jsx(ClientOnboardingInnerV1, _object_spread({}, props))
        })
    });
}
export function ClientOnboardingRouted(props) {
    var _ff_data;
    const { relationship } = props;
    const history = useHistory();
    const ff = useStore(selectors.ff.featureFlags);
    const businessId = useStore(selectors.user.businessId);
    const ffRelationshipVendorId = useStore((state)=>state.ff.relationshipVendorId);
    const { ff: { setRelationshipVendorId } } = useStoreActions();
    const { getFromStorage } = useCobStorage(relationship);
    const role = useStore(selectors.user.role);
    const userId = useStore(selectors.user.id);
    const fetchingFF = !ff.data || !ffRelationshipVendorId;
    const relationshipStatus = relationshipModel.status(relationship);
    const relationshipVendorId = relationshipModel.vendorID(relationship);
    const isVendor = useStore(selectors.user.isVendor);
    const [clientOnboardingNavigateState, dispatch] = useReducer(clientOnboardingNavigateStateReducer, {
        urlToNavigate: null,
        relationshipStatus: relationshipModel.status(relationship),
        role,
        relationshipId: relationshipModel.id(relationship),
        hasStartedWithPendingStatus: relationshipModel.isPending(relationship),
        isPrimary: relationshipModel.isBuyer(relationship, userId),
        vendorName: relationshipModel.vendorName(relationship),
        isVendor
    });
    useEffect(()=>{
        var _ff_data;
        if (!clientOnboardingNavigateState.urlToNavigate) return;
        // V2 flow: show signed success in-card instead of navigating
        if ((_ff_data = ff.data) === null || _ff_data === void 0 ? void 0 : _ff_data.CLIENT_ONBOARDING_NEW_FLOW) return;
        const navigate = isAllowedToGoBack(clientOnboardingNavigateState.urlToNavigate) ? history.push : history.replace;
        navigate(clientOnboardingNavigateState.urlToNavigate, clientOnboardingNavigateState.state);
    }, [
        clientOnboardingNavigateState.urlToNavigate,
        clientOnboardingNavigateState.state,
        (_ff_data = ff.data) === null || _ff_data === void 0 ? void 0 : _ff_data.CLIENT_ONBOARDING_NEW_FLOW
    ]);
    useEffect(()=>{
        dispatch({
            type: 'RelationshipStatusChanged',
            status: relationshipStatus
        });
    }, [
        relationshipStatus
    ]);
    useEffect(()=>{
        setRelationshipVendorId(relationshipVendorId);
        return ()=>{
            setRelationshipVendorId();
        };
    }, [
        relationshipVendorId
    ]);
    if (fetchingFF) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            name: 'fetchFF',
            show: true
        });
    }
    return /*#__PURE__*/ _jsxs(Switch, {
        children: [
            /*#__PURE__*/ _jsx(Route, {
                path: "".concat(routes.clientOnboarding(), "/:stepId"),
                render: (param)=>{
                    let { history, match } = param;
                    var _ff_data;
                    const isV2 = (_ff_data = ff.data) === null || _ff_data === void 0 ? void 0 : _ff_data.CLIENT_ONBOARDING_NEW_FLOW;
                    const initialBranding = isV2 ? _object_spread_props(_object_spread({}, relationship.vendor.branding), {
                        progressBarComplete: relationship.vendor.branding.buttonPrimary
                    }) : relationship.vendor.branding;
                    return /*#__PURE__*/ _jsx(CobBrandingProvider, {
                        initialBranding: initialBranding,
                        children: /*#__PURE__*/ _jsx(ClientOnboarding, {
                            previewState: // A vendor can enter here by copying the proposal-link and pasting it in a new tab while logged in.
                            relationship && relationship.vendor.id === businessId && !relationship.isSample ? PreviewState.Preview : undefined,
                            refresh: props.refresh,
                            relationship: relationship,
                            currentStep: match.params.stepId,
                            setCurrentStep: (step)=>history.push(routes.clientOnboarding(relationshipModel.id(relationship), step))
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.clientOnboarding(),
                children: (param)=>{
                    let { match } = param;
                    var _getFromStorage;
                    return /*#__PURE__*/ _jsx(Redirect, {
                        to: "".concat(match.url, "/").concat((_getFromStorage = getFromStorage('last_step')) !== null && _getFromStorage !== void 0 ? _getFromStorage : COBStep.welcome)
                    });
                }
            })
        ]
    });
}
export function ClientOnboardingQueryWrapper(props) {
    const { relationshipId } = props.match.params;
    const businessId = useStore(selectors.user.businessId);
    const { api } = useStoreActions();
    const overlayContext = useOverlayContext();
    useDidUpdate(()=>{
        overlayContext.stopLoader(changeBusinessLoader);
    }, [
        businessId
    ]);
    async function fetchData() {
        const [relationship, membersResponse] = await Promise.all([
            api.relationship.get(relationshipId),
            api.member.getAll()
        ]);
        const shouldSetAccountants = requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess);
        return {
            relationship,
            accountants: shouldSetAccountants ? membersResponse.accountants : []
        };
    }
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchData,
        renderChild: (param)=>{
            let { data, refresh } = param;
            if (data.relationship.withdrawnForEditing) {
                return /*#__PURE__*/ _jsx(WithdrawnForEditingPage, {
                    vendorName: relationshipModel.vendorName(data.relationship),
                    onRefresh: refresh
                });
            }
            return /*#__PURE__*/ _jsx(ClientOnboardingRouted, {
                relationship: data.relationship,
                refresh: refresh
            });
        }
    });
}
export default function ClientOnboardingContextWrapper(props) {
    return /*#__PURE__*/ _jsx(CobStorageProvider, {
        children: /*#__PURE__*/ _jsx(ClientOnboardingQueryWrapper, _object_spread({}, props))
    });
}
